import type { MapChestData, MapEventData } from '../render/tilemap.js';

/**
 * As faixas de id dos mapas — as mesmas do servidor (`AdminMapController`) e do
 * pipeline (`MAP_ID_FLOOR` em ozimport/pack.py):
 *
 *   1–999       os mapas do acervo original (arquivo)
 *   1000–9999   os mapas criados no editor (banco)
 *   10000+      os mapas dos jogos importados (arquivo)
 *
 * Só a faixa do meio é do editor. Tratar "id ≥ 1000" como "meu" punha os mapas
 * de pacote do lado errado: o botão Excluir aparecia para eles, e salvar ou
 * jogar gravava por cima do mapa do jogo importado em vez de criar uma cópia.
 */
export const EDITOR_MAP_MIN_ID = 1000;
export const PACK_MAP_MIN_ID = 10000;

export function isEditorMapId(id: number | undefined | null): id is number {
  return typeof id === 'number' && id >= EDITOR_MAP_MIN_ID && id < PACK_MAP_MIN_ID;
}

export interface MapListItem {
  readonly id: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly tilesetId: number;
  readonly isCustom: boolean;
  readonly updatedAt?: string | undefined;
}

export interface MapPlacement {
  readonly id?: number | undefined;
  readonly name: string;
  readonly team: 'jogador' | 'inimigo' | 'aliado';
  readonly className: string;
  readonly weaponName: string;
  readonly level?: number | undefined;
  readonly at: { x: number; y: number };
  readonly isCommander?: boolean | undefined;
  readonly aiProfile?: string | undefined;
  readonly portrait?: { sheet: string; index: number } | undefined;
  readonly sprite?: { sheet: string; index: number } | undefined;
  readonly battler?: string | undefined;
}

export interface MapObjective {
  readonly kind: 'derrotar_todos' | 'derrotar_comandante' | 'tomar_posicao' | 'sobreviver';
  readonly turns?: number | undefined;
  readonly at?: { x: number; y: number } | undefined;
}

export interface CustomMapData {
  readonly id?: number | undefined;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly tilesetId: number;
  readonly layers: readonly (readonly number[])[];
  readonly shadows?: readonly number[] | undefined;
  readonly terrain: readonly number[];
  readonly terrainTypes: readonly string[];
  readonly placements?: readonly MapPlacement[] | undefined;
  readonly objective?: MapObjective | undefined;
  readonly isCustom?: boolean | undefined;
  /* --- ambiente do mapa: viaja intacto do banco ao jogo, pelo editor --- */
  readonly bgm?: string | undefined;
  readonly battleback?: string | undefined;
  readonly battleback2?: string | undefined;
  readonly teleports?: readonly { a: { x: number; y: number }; b: { x: number; y: number } }[] | undefined;
  /** Paisagem por trás do mapa; e os elementos desenhados (portas, baús, gente). */
  readonly parallax?: string | undefined;
  readonly events?: readonly MapEventData[] | undefined;
  /** Os baús que o mapa oferece. Ver `MapChestData` em `render/tilemap.ts`. */
  readonly chests?: readonly MapChestData[] | undefined;
}

export async function fetchMapList(): Promise<MapListItem[]> {
  const res = await fetch('/api/maps');
  if (!res.ok) {
    throw new Error(`Falha ao carregar lista de mapas: HTTP ${res.status}`);
  }
  const json = (await res.json()) as { maps: MapListItem[] };
  return json.maps;
}

export async function fetchMapData(id: number): Promise<CustomMapData> {
  const res = await fetch(`/api/maps/${id}`);
  if (!res.ok) {
    throw new Error(`Falha ao carregar mapa #${id}: HTTP ${res.status}`);
  }
  return (await res.json()) as CustomMapData;
}

export async function saveMap(
  data: CustomMapData,
): Promise<{ ok: boolean; map?: MapListItem; message?: string }> {
  // Mapa do acervo ou de pacote nunca é regravado pelo editor: vira cópia nova.
  const isUpdate = isEditorMapId(data.id);
  const url = isUpdate ? `/api/admin/maps/${data.id}` : '/api/admin/maps';
  const method = isUpdate ? 'PUT' : 'POST';

  try {
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    const json = await res.json();
    if (!res.ok) {
      return { ok: false, message: json.error?.message ?? `Erro HTTP ${res.status}` };
    }
    return { ok: true, map: json.map };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Tira o mapa da lista — seja ele do editor, do acervo ou de um jogo importado.
 *
 * `hidden` diz qual das duas exclusões aconteceu do lado de lá: o mapa do
 * editor some do banco, e o de arquivo ganha uma lápide (o pipeline reescreve
 * o acervo a cada importação, então apagar o arquivo não colaria). Quem chama
 * usa isso para contar ao autor que salvar de novo naquele id traz o mapa de
 * volta.
 */
export async function deleteMap(
  id: number,
): Promise<{ ok: boolean; message?: string; hidden?: boolean }> {
  try {
    const res = await fetch(`/api/admin/maps/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const json = await res.json();
    if (!res.ok) {
      return { ok: false, message: json.error?.message ?? `Erro HTTP ${res.status}` };
    }
    return { ok: true, hidden: Boolean(json.hidden) };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}
